import { memo, useCallback, useMemo, useState, type MouseEvent } from "react";
import { z } from "zod";

import type { VoucherForCampaignUserItemInput } from "@/lib/gql/__generated__/graphql";

import { Button } from "@/components/ui/button/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog/dialog";
import { InputTextField } from "@/components/ui/form/fields/input-field/input-text";
import { Label } from "@/components/ui/label/label";

type Props = {
	open: boolean;
	setOpen: (open: boolean) => void;

	addItem: (item: VoucherForCampaignUserItemInput) => void;
};

type GuestInfoType = {
	guestEmail?: {
		value: string | undefined;
		errorMessage: string;
	};
	guestName: {
		value: string;
		errorMessage: string;
	};
	guestTel: {
		value: string;
		errorMessage: string;
	};
};

const defaultGuestInfo: GuestInfoType = {
	guestName: { value: "", errorMessage: "" },
	guestEmail: { value: undefined, errorMessage: "" },
	guestTel: { value: "", errorMessage: "" },
};

const guestInfoZodSchema = z.object({
	guestName: z.string().min(1).max(100),
	guestTel: z.string().max(15),
	guestEmail: z.string().email().or(z.literal(undefined)),
});
type GuestInfoZodSchemaType = z.infer<typeof guestInfoZodSchema>;

export const DialogInputGuestInfo = memo(({ open, setOpen, addItem }: Props) => {
	const [guestInfo, setGuestInfo] = useState<GuestInfoType>(defaultGuestInfo);
	const onResetGuestInfo = useCallback(() => {
		setGuestInfo(defaultGuestInfo);
	}, []);

	const disabledSaveButton = useMemo(() => {
		const res = guestInfoZodSchema.safeParse({
			guestName: guestInfo.guestName.value,
			guestTel: guestInfo.guestTel.value,
			guestEmail: guestInfo.guestEmail?.value || undefined,
		} satisfies GuestInfoZodSchemaType);

		return !res.success;
	}, [guestInfo]);

	const handleOpenChange = useCallback(
		(open: boolean) => {
			if (!open) {
				onResetGuestInfo();
			}
			setOpen(open);
		},
		[onResetGuestInfo, setOpen],
	);

	const handleClickSaveButton = useCallback(
		(e: MouseEvent) => {
			e.stopPropagation();

			const item = {
				guestName: guestInfo.guestName.value,
				guestTel: guestInfo.guestTel.value,
				guestEmail: guestInfo.guestEmail?.value || undefined,
			};

			addItem(item);
			onResetGuestInfo();
			setOpen(false);
		},
		[addItem, guestInfo, onResetGuestInfo, setOpen],
	);

	const onSetGuestInfo = useCallback(
		({ key, value }: { key: keyof GuestInfoType; value: string }) => {
			const currentGuestInfo = {
				...(Object.fromEntries(
					Object.entries(guestInfo)
						.filter(([k]) => k !== key)
						.map(([k, v]) => [k, v.value]),
				) as Record<keyof GuestInfoType, string>),
				[key]: value,
			} satisfies GuestInfoZodSchemaType;

			const result = guestInfoZodSchema.safeParse(currentGuestInfo);

			const errorMessage = result.error?.errors.find((error) => error.path.includes(key))?.message ?? "";

			setGuestInfo((prev) => {
				const newList = { ...prev, [key]: { value, errorMessage } };
				return newList;
			});
		},
		[guestInfo],
	);

	const handleChangeGuestName = useCallback(
		(value: string) => {
			onSetGuestInfo({ key: "guestName", value });
		},
		[onSetGuestInfo],
	);

	const handleChangeGuestEmail = useCallback(
		(value: string) => {
			onSetGuestInfo({ key: "guestEmail", value });
		},
		[onSetGuestInfo],
	);

	const handleChangeGuestTel = useCallback(
		(value: string) => {
			onSetGuestInfo({ key: "guestTel", value });
		},
		[onSetGuestInfo],
	);

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="space-y-4">
				<DialogHeader>
					<DialogTitle>ゲスト情報入力</DialogTitle>
				</DialogHeader>
				<div className="space-y-8">
					<div className="flex flex-col gap-y-6">
						<InputTextField
							value={guestInfo?.guestName.value}
							handleChange={handleChangeGuestName}
							errorMessage={guestInfo?.guestName.errorMessage}
							label={
								<div className="flex items-center gap-x-3">
									<p>名前</p>
									<Label text="必須" size="sm" />
								</div>
							}
						/>
						<InputTextField
							value={guestInfo?.guestEmail?.value}
							handleChange={handleChangeGuestEmail}
							errorMessage={guestInfo?.guestEmail?.errorMessage}
							label="メールアドレス"
						/>
						<InputTextField
							value={guestInfo?.guestTel.value}
							handleChange={handleChangeGuestTel}
							errorMessage={guestInfo?.guestTel.errorMessage}
							label="電話番号"
						/>
					</div>

					<div className="flex justify-end">
						<Button
							type={"button"}
							size={"lg"}
							onClick={handleClickSaveButton}
							disabled={disabledSaveButton}
						>
							追加
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
});

DialogInputGuestInfo.displayName = "DialogInputGuestInfo";
